import { Suspense } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/base/ScrollToTop';
import { SEO } from '../../utils/seo';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "就労支援サービス",
    "provider": {
      "@type": "LocalBusiness",
      "name": "おかやま就労支援事業所",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "○○町1-2-3",
        "addressLocality": "岡山市北区",
        "addressRegion": "岡山県",
        "addressCountry": "JP"
      },
      "telephone": "+81-86-012-3456"
    },
    "serviceType": "就労継続支援、就労移行支援、障害者雇用支援",
    "areaServed": {
      "@type": "City",
      "name": "岡山市"
    },
    "description": "個別支援計画、職業訓練、コミュニケーションスキル、就職活動支援、就職後フォローなど包括的な就労支援サービスを提供"
  };

  const programs = [
    {
      id: 1,
      title: '個別支援計画',
      description: '一人ひとりの目標や適性に合わせて、専門スタッフが個別支援計画を作成します。定期的な面談を通じて、計画の見直しや調整を行い、着実にステップアップできるようサポートします。',
      features: [
        '初回アセスメントによる現状把握',
        '具体的な目標設定とスケジュール作成',
        '月1回の定期面談と計画の見直し',
        '家族や関係機関との連携サポート',
      ],
      image: 'https://public.readdy.ai/ai/img_res/8ce0045d56acaa11fb8bb53555fc36a6.jpg',
    },
    {
      id: 2,
      title: '職業訓練プログラム',
      description: 'パソコン操作、事務作業、軽作業など、実践的なスキルを身につけるための多様なプログラムをご用意しています。基礎から応用まで、段階的に学べる環境を整えています。',
      features: [
        'パソコン基礎（Word、Excel、メール）',
        '事務作業訓練（データ入力、書類整理）',
        '軽作業訓練（梱包、仕分け、清掃）',
        '専門スキル訓練（希望に応じて）',
      ],
      image: 'https://readdy.ai/api/search-image?query=Japanese%20person%20practicing%20computer%20skills%20training%20at%20workstation%2C%20focused%20on%20typing%20and%20using%20office%20software%2C%20instructor%20standing%20nearby%20providing%20guidance%2C%20bright%20modern%20training%20room%20with%20multiple%20computers%2C%20organized%20learning%20environment%2C%20professional%20atmosphere%2C%20natural%20lighting%2C%20contemporary%20office%20furniture%2C%20sense%20of%20skill%20development%20and%20progress&width=1000&height=700&seq=service002&orientation=landscape',
    },
    {
      id: 3,
      title: 'コミュニケーションスキル',
      description: '職場で必要なコミュニケーション能力を養います。グループワークやロールプレイを通じて、実践的なスキルを身につけることができます。',
      features: [
        '基本的な挨拶や報告・連絡・相談',
        'グループワークによる協調性の向上',
        'ビジネスマナーの習得',
        'ストレスマネジメント',
      ],
      image: 'https://readdy.ai/api/search-image?query=Group%20of%20Japanese%20people%20participating%20in%20communication%20skills%20workshop%2C%20sitting%20in%20circle%20having%20friendly%20discussion%2C%20diverse%20participants%20engaging%20in%20conversation%2C%20supportive%20facilitator%20leading%20session%2C%20bright%20comfortable%20meeting%20room%2C%20collaborative%20atmosphere%2C%20natural%20expressions%20of%20listening%20and%20speaking%2C%20modern%20interior%20with%20warm%20lighting&width=1000&height=700&seq=service003&orientation=landscape',
    },
    {
      id: 4,
      title: '就職活動支援',
      description: '履歴書の書き方から面接対策まで、就職活動に必要なすべてをサポートします。企業とのマッチングや職場見学の同行も行います。',
      features: [
        '履歴書・職務経歴書の作成支援',
        '模擬面接と面接対策',
        '企業とのマッチング',
        '職場見学・実習の同行',
      ],
      image: 'https://readdy.ai/api/search-image?query=Japanese%20employment%20counselor%20helping%20client%20prepare%20for%20job%20interview%2C%20practicing%20interview%20skills%20together%20at%20desk%2C%20resume%20documents%20visible%2C%20encouraging%20supportive%20atmosphere%2C%20professional%20office%20setting%2C%20both%20people%20showing%20positive%20expressions%2C%20natural%20lighting%2C%20modern%20workspace%2C%20sense%20of%20preparation%20and%20confidence%20building&width=1000&height=700&seq=service004&orientation=landscape',
    },
    {
      id: 5,
      title: '就職後のフォロー',
      description: '就職後も定期的に職場を訪問し、安心して働き続けられるようサポートします。職場での困りごとや悩みの相談にも対応します。',
      features: [
        '定期的な職場訪問（月1〜2回）',
        '職場での困りごと相談',
        '企業との調整・連携',
        '最長6ヶ月間の継続サポート',
      ],
      image: 'https://readdy.ai/api/search-image?query=Japanese%20employment%20support%20staff%20visiting%20client%20at%20their%20workplace%2C%20having%20friendly%20check-in%20conversation%20in%20office%20break%20room%2C%20both%20smiling%20and%20showing%20positive%20rapport%2C%20professional%20business%20environment%2C%20natural%20workplace%20setting%2C%20sense%20of%20ongoing%20support%20and%20success%2C%20bright%20lighting%2C%20contemporary%20office%20interior&width=1000&height=700&seq=service005&orientation=landscape',
    },
  ];

  const features = [
    {
      id: 1,
      title: '個別支援計画',
      description: 'あなたの目標に合わせた、オーダーメイドの支援プログラムを作成します。',
      image: 'https://readdy.ai/api/search-image?query=Close-up%20of%20Japanese%20staff%20and%20client%20hands%20pointing%20at%20individual%20support%20plan%20document%20on%20desk%2C%20planning%20meeting%20scene%2C%20professional%20consultation%20atmosphere%2C%20documents%20and%20pen%20visible%2C%20warm%20office%20lighting%2C%20sense%20of%20personalized%20care%20and%20attention%20to%20detail&width=800&height=600&seq=feature001&orientation=landscape',
    },
    {
      id: 2,
      title: '職業訓練プログラム',
      label: '人気',
      description: '実践的なスキルを身につけ、自信を持って就職活動に臨めるようサポートします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20person%20successfully%20completing%20work%20training%20task%2C%20showing%20satisfied%20expression%20of%20accomplishment%2C%20modern%20training%20facility%2C%20bright%20workspace%20with%20equipment%2C%20sense%20of%20skill%20mastery%20and%20confidence%2C%20professional%20environment%2C%20natural%20lighting&width=800&height=600&seq=feature002&orientation=landscape',
    },
    {
      id: 3,
      title: '就職後のフォロー',
      description: '就職がゴールではありません。長く安心して働けるよう、継続的にサポートします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20employment%20support%20staff%20shaking%20hands%20with%20successfully%20employed%20client%20in%20workplace%2C%20both%20showing%20happy%20satisfied%20expressions%2C%20professional%20office%20background%2C%20sense%20of%20achievement%20and%20ongoing%20partnership%2C%20bright%20natural%20lighting%2C%20contemporary%20business%20setting&width=800&height=600&seq=feature003&orientation=landscape',
    },
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEO
        title="事業内容｜個別支援から就職後フォローまで｜おかやま就労支援事業所"
        description="岡山市の就労支援事業所の事業内容。個別支援計画、職業訓練プログラム、コミュニケーションスキル、就職活動支援、就職後フォローなど、一人ひとりに合わせた包括的な支援を提供。障害や病気があっても自分らしく働くための充実したプログラム。"
        keywords="就労支援,事業内容,職業訓練,個別支援,就職支援,岡山市"
        schema={schema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-bg-ivory">
        <Header />
        <main>
          {/* Hero Section */}
          <section className="relative h-96 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20people%20with%20disabilities%20working%20together%20in%20group%20collaboration%20session%2C%20diverse%20team%20members%20cooperating%20on%20project%2C%20bright%20modern%20workspace%2C%20supportive%20encouraging%20atmosphere%2C%20natural%20expressions%20of%20teamwork%20and%20mutual%20support%2C%20contemporary%20office%20interior%2C%20large%20windows%20with%20natural%20light%2C%20sense%20of%20community%20and%20shared%20purpose&width=1920&height=800&seq=servicehero001&orientation=landscape"
                alt="事業内容"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            </div>
            <div className="relative z-10 text-center text-white space-y-4">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">事業内容</h1>
              <p className="text-sm text-white/80">トップ &gt; 事業内容</p>
            </div>
          </section>

          {/* Overview Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
                <div>
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-text-dark leading-tight mb-6">
                    私たちが提供する<br /><strong>支援</strong>
                  </h2>
                </div>
                <div className="space-y-4 text-text-medium leading-relaxed">
                  <p>
                    おかやま就労支援事業所では、<strong>障害や病気</strong>を抱える方々が、自分らしく働くための包括的な支援を提供しています。
                  </p>
                  <p>
                    一人ひとりの状況や目標に合わせた<strong>個別支援計画</strong>の作成から、実践的な<strong>職業訓練</strong>、<strong>就職活動のサポート</strong>、そして<strong>就職後のフォロー</strong>まで、一貫した支援体制を整えています。
                  </p>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {features.map((feature, index) => (
                  <div
                    key={feature.id}
                    className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow ${
                      index === 0 ? 'bg-primary text-white' : 'bg-white'
                    }`}
                  >
                    {index === 0 ? (
                      <>
                        <div className="p-6 space-y-4">
                          <h3 className="text-2xl font-bold">{feature.title}</h3>
                          <p className="text-white/90 leading-relaxed">{feature.description}</p>
                        </div>
                        <div className="relative w-full h-48">
                          <img
                            src={feature.image}
                            alt={feature.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {feature.label && (
                          <div className="px-6 pt-4">
                            <span className="bg-primary-light text-primary text-xs px-3 py-1 rounded-full">
                              {feature.label}
                            </span>
                          </div>
                        )}
                        <div className="relative w-full h-48">
                          <img
                            src={feature.image}
                            alt={feature.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="p-6 space-y-3">
                          <h3 className="text-xl font-bold text-text-dark">{feature.title}</h3>
                          <p className="text-text-medium text-sm leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Detailed Programs Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-bg-beige">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <p className="text-primary text-sm font-medium tracking-wider mb-3">
                  （詳細プログラム）
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-text-dark">
                  充実した<strong>支援プログラム</strong>
                </h2>
              </div>

              <div className="space-y-16 lg:space-y-24">
                {programs.map((program, index) => (
                  <div
                    key={program.id}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                      index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                    }`}
                  >
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <div className="relative rounded-2xl overflow-hidden shadow-xl h-80 lg:h-96">
                        <img
                          src={program.image}
                          alt={program.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-text-dark">
                        {program.title}
                      </h3>
                      <p className="text-text-medium leading-relaxed">{program.description}</p>
                      <ul className="space-y-3">
                        {program.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-primary text-xl mr-3 flex-shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                            <span className="text-text-medium">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Director's Message Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <p className="text-primary text-sm font-medium tracking-wider mb-3">
                  （代表メッセージ）
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-text-dark">
                  代表からの<strong>ご挨拶</strong>
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-2">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20middle-aged%20Japanese%20male%20director%20in%20formal%20dark%20navy%20business%20suit%20and%20tie%2C%20standing%20confidently%20with%20warm%20welcoming%20smile%2C%20clean%20white%20background%2C%20executive%20headshot%20style%2C%20professional%20lighting%2C%20sense%20of%20trustworthiness%20and%20leadership%2C%20compassionate%20expression%2C%20contemporary%20business%20portrait%20photography&width=800&height=1000&seq=director001&orientation=portrait"
                      alt="代表取締役 岡山 太郎"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="text-center mt-6">
                    <p className="text-text-dark font-bold text-xl mb-1">代表取締役</p>
                    <p className="text-text-dark font-bold text-2xl">岡山 太郎</p>
                  </div>
                </div>

                <div className="lg:col-span-3 space-y-6 text-text-medium leading-relaxed">
                  <p>
                    おかやま就労支援事業所のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                  </p>
                  <p>
                    私たちは、<strong>障害や病気を抱える方々が、自分らしく働き、社会の一員として活躍できる</strong>ことを心から願い、日々の支援活動に取り組んでおります。
                  </p>
                  <p>
                    就労支援の現場で20年以上の経験を積む中で、一人ひとりの可能性は無限大であることを実感してまいりました。適切な支援と環境があれば、誰もが自分の強みを活かし、社会に貢献できると確信しております。
                  </p>
                  <p>
                    当事業所では、<strong>個別支援計画</strong>を基に、利用者様一人ひとりの目標や適性に合わせたきめ細やかな支援を提供しております。職業訓練から就職活動のサポート、そして就職後のフォローまで、一貫した支援体制を整えております。
                  </p>
                  <p>
                    また、スタッフ一同、利用者様やご家族様との信頼関係を大切にし、<strong>寄り添う支援</strong>を心がけております。どんな小さなことでも、お気軽にご相談ください。
                  </p>
                  <p>
                    皆様の「働きたい」という想いを、私たちが全力でサポートいたします。一緒に、新しい一歩を踏み出しましょう。
                  </p>
                  <div className="pt-4">
                    <p className="text-text-dark font-medium">おかやま就労支援事業所</p>
                    <p className="text-text-dark font-bold text-xl">代表取締役　岡山 太郎</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </Suspense>
  );
};

export default ServicesPage;
